import React, { useMemo, useState } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from '../DesignSystems/New/StylishNewSearchBar';
import PratusSelect, { OptionType } from '../DesignSystems/New/PratusSelect';
import { StylishNewChip } from 'components/DesignSystems/New/StylishNewChip';
import FormExportCard from './FormExportCard';
import { selectIncident, useAppSelector } from '../../slices/commonSelectors';
import { useIncidentInfo } from './hooks/incidentInfoHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useCurrentOperationalPeriod } from './hooks/incidentHooks';
import {
  PLANNING_PHASES,
  RESPONSIBLE_ROLE,
  ResponsibleRoleType,
} from './planningView/USCGPage';
import { ICS_FORMS_METADATA } from './ICSConstants';

const ICSFormsPage: React.FC = () => {
  const currentIncident = useAppSelector(selectIncident);
  const { data: incidentInfo } = useIncidentInfo();
  const { data: currentPeriod } = useCurrentOperationalPeriod();
  const periodNumber = currentPeriod?.period_number ?? 0;
  const { exportPaginatedIAP } = useIcsFormPdfPreview();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<OptionType[]>([]);
  const [selectedPhases, setSelectedPhases] = useState<OptionType[]>([]);

  const handleExportPaginatedIAP = () => {
    exportPaginatedIAP({ periodNumber });
  };

  const roleOptions = Object.values(RESPONSIBLE_ROLE).map((role) => ({
    label: role,
    value: role,
  }));

  const phaseOptions = PLANNING_PHASES.map((phase) => ({
    label: phase.title,
    value: phase.title,
  }));

  const filterPills: FilterPill[] = useMemo(() => {
    const pills: FilterPill[] = [];

    if (searchTerm) {
      pills.push({
        id: 'search',

        label: `Search: ${searchTerm}`,
        value: searchTerm,
      });
    }

    selectedRoles.forEach((role) => {
      pills.push({
        id: `role-${role.value}`,

        label: role.label as string,
        value: role.value as string,
      });
    });

    selectedPhases.forEach((phase) => {
      pills.push({
        id: `phase-${phase.value}`,

        label: phase.label as string,
        value: phase.value as string,
      });
    });

    return pills;
  }, [searchTerm, selectedRoles, selectedPhases]);

  const handleRemoveFilter = (filterId: string) => {
    if (filterId === 'search') {
      setSearchTerm('');
    } else if (filterId.startsWith('role-')) {
      const roleValue = filterId.replace('role-', '');
      setSelectedRoles(selectedRoles.filter((r) => r.value !== roleValue));
    } else if (filterId.startsWith('phase-')) {
      const phaseValue = filterId.replace('phase-', '');
      setSelectedPhases(selectedPhases.filter((p) => p.value !== phaseValue));
    }
  };

  const filteredForms = useMemo(() => {
    let filtered = ICS_FORMS_METADATA;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (form) =>
          form.label.toLowerCase().includes(searchLower) ||
          form.formName.toLowerCase().includes(searchLower)
      );
    }

    if (selectedRoles.length > 0) {
      const selectedRoleValues = selectedRoles.map(
        (r) => r.value as ResponsibleRoleType
      );
      filtered = filtered.filter((form) =>
        form.responsibleRoles.some((role) => selectedRoleValues.includes(role))
      );
    }

    if (selectedPhases.length > 0) {
      const selectedPhaseValues = selectedPhases.map((p) => p.value as string);
      filtered = filtered.filter((form) =>
        form.planningPhases.some((phase) => selectedPhaseValues.includes(phase))
      );
    }

    return filtered;
  }, [searchTerm, selectedRoles, selectedPhases]);

  return (
    <div className="iap-forms-page mx-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>ICS Forms</h2>
      </div>

      <Row className="align-items-center">
        <Col xs={12} lg={2}>
          <StylishNewSearchBar
            value={searchTerm}
            onChangeSearchTerm={setSearchTerm}
            placeholder="Search form names..."
            icon="search"
            label="Search"
          />
        </Col>
        <Col xs={12} lg={2}>
          <PratusSelect
            label="Role"
            options={roleOptions}
            value={selectedRoles}
            onChange={(value) => setSelectedRoles(value as OptionType[])}
            isMulti={true}
            placeholder="Select roles..."
            inputId="role-filter"
          />
        </Col>
        <Col xs={12} lg={2}>
          <PratusSelect
            label="Planning Phase"
            options={phaseOptions}
            value={selectedPhases}
            onChange={(value) => setSelectedPhases(value as OptionType[])}
            isMulti={true}
            placeholder="Select phases..."
            inputId="phase-filter"
          />
        </Col>
        <Col xs={12} lg={2} className="ms-auto">
          <StylishNewButton
            className="button--secondary w-100"
            onClick={handleExportPaginatedIAP}
          >
            Preview IAP
          </StylishNewButton>
        </Col>
      </Row>

      {filterPills.length > 0 && (
        <div className="mt-3">
          <FilterPills filters={filterPills} onRemove={handleRemoveFilter} />
        </div>
      )}

      <div className="d-flex flex-column mt-4 gap-4">
        {filteredForms.map((form) => (
          <FormExportCard
            key={form.formName}
            formName={form.formName}
            label={form.label}
            description={form.description}
            responsibleRoles={form.responsibleRoles}
            planningPhases={form.planningPhases}
            canShareWithTeams={
              form.formName === 'ICS_201-CG'
                ? !!currentIncident?.sharepoint_location
                : false
            }
            imageData={
              form.formName === 'ICS_201-CG'
                ? incidentInfo?.mapSketchUrl
                : undefined
            }
          />
        ))}
      </div>

      {filteredForms.length === 0 && (
        <Card className="bg-bgdark mt-4">
          <Card.Body>
            <div className="text-muted text-center">
              No ICS forms match your current filters.
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ICSFormsPage;

interface FilterPill {
  id: string;
  label: string;
  value: string;
}

interface FilterPillsProps {
  filters: FilterPill[];
  onRemove: (filterId: string) => void;
}

export const FilterPills: React.FC<FilterPillsProps> = ({
  filters,
  onRemove,
}) => {
  if (filters.length === 0) {
    return null;
  }

  return (
    <div className={'d-flex mb-3 flex-wrap gap-3'}>
      {filters.map((filter) => (
        <StylishNewChip
          key={filter.id}
          label={filter.label}
          handleDelete={() => onRemove(filter.id)}
          className="px-2"
        />
      ))}
    </div>
  );
};
